import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  RefreshControl,
  ActivityIndicator
} from 'react-native';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import Header from '../../Components/Header';
import ScreenWrapper from '../../Components/ScreenWrapper';
import PollCard from '../../Components/PollCard';
import Loading from '../../Components/Loading';
import { theme } from '../../constants/theme';
import { useAuth } from '../../Utils/AuthProvider';
import { hp, wp } from '../../helpers/common';
import { fetchPollsWithCursor, testSupabaseConnection } from '../../services/pollService';

const PollsList = () => {
  const navigation = useNavigation();
  const { userData } = useAuth();
  
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [cursor, setCursor] = useState(null);
  const [error, setError] = useState(null);

  useFocusEffect(
    useCallback(() => {
      loadPolls(true);
    }, [])
  );

  const loadPolls = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
        setCursor(null);
        setHasMore(true);
      } else {
        setLoadingMore(true);
      }

      // First test the connection and table structure
      const connectionTest = await testSupabaseConnection();
      
      const result = await fetchPollsWithCursor(10, null, null, isRefresh ? null : cursor);
      
      if (result.success) {
        if (isRefresh) {
          setPolls(result.data);
        } else {
          setPolls(prev => [...prev, ...result.data]);
        }
        
        setCursor(result.nextCursor);
        setHasMore(result.hasMore);
        setError(null);
      } else {
        setError(result.msg);
      }
    } catch (error) {
      setError('Could not load polls: ' + error.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
      setLoadingMore(false);
    }
  };

  const handleRefresh = () => {
    loadPolls(true);
  };

  const handleLoadMore = () => {
    if (hasMore && !loadingMore) {
      loadPolls(false);
    }
  };

  const handlePollPress = (poll) => {
    navigation.navigate('PollDetails', { pollId: poll.id });
  };

  const handlePollDelete = (pollId) => {
    setPolls(prev => prev.filter(poll => poll.id !== pollId));
  };

  const handlePollEdit = (poll) => {
    navigation.navigate('CreatePoll', { 
      pollId: poll.id,
      editMode: true,
      initialData: poll
    });
  };

  const renderPoll = ({ item }) => (
    <PollCard
      item={item}
      currentUser={userData}
      onPress={() => handlePollPress(item)}
      onDelete={handlePollDelete}
      onEdit={() => handlePollEdit(item)}
      showDelete={true}
    />
  );

  const renderFooter = () => {
    if (!loadingMore) return null;
    
    return (
      <View style={styles.footerLoader}>
        <ActivityIndicator size="small" color={theme.colors.primary} />
        <Text style={styles.footerText}>Loading more polls...</Text>
      </View>
    );
  };

  const renderEmpty = () => (
    <View style={styles.emptyContainer}>
      <Text style={styles.emptyTitle}>No Polls Yet</Text>
      <Text style={styles.emptyDescription}>
        Be the first to create a poll and engage with the community!
      </Text>
      <TouchableOpacity
        style={styles.createButton}
        onPress={() => navigation.navigate('CreatePoll')}
      >
        <Text style={styles.createButtonText}>Create First Poll</Text>
      </TouchableOpacity>
    </View>
  );

  const renderError = () => (
    <View style={styles.errorContainer}>
      <Text style={styles.errorTitle}>Something went wrong</Text>
      <Text style={styles.errorDescription}>{error}</Text>
      <TouchableOpacity style={styles.retryButton} onPress={handleRefresh}>
        <Text style={styles.retryButtonText}>Try Again</Text>
      </TouchableOpacity>
    </View>
  );

  if (loading && polls.length === 0) {
    return (
      <ScreenWrapper>
        <Header title="Polls" showBackButton />
        <Loading />
      </ScreenWrapper>
    );
  }

  if (error && polls.length === 0) {
    return (
      <ScreenWrapper>
        <Header title="Polls" showBackButton />
        {renderError()}
      </ScreenWrapper>
    );
  }

  return (
    <ScreenWrapper>
      <Header
        title="All Polls"
        showBackButton
      />
      
      {/* Create Poll Button */}
      <View style={styles.headerActions}>
        <TouchableOpacity
          onPress={() => navigation.navigate('CreatePoll')}
          style={styles.headerButton}
        >
          <Text style={styles.headerButtonText}>Create Poll</Text>
        </TouchableOpacity>
      </View>

      <FlatList
        data={polls}
        renderItem={renderPoll}
        keyExtractor={(item) => item.id}
        contentContainerStyle={styles.listContainer}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            colors={[theme.colors.primary]}
            tintColor={theme.colors.primary}
          />
        }
        onEndReached={handleLoadMore}
        onEndReachedThreshold={0.1}
        ListFooterComponent={renderFooter}
        ListEmptyComponent={renderEmpty}
        showsVerticalScrollIndicator={false}
      />
    </ScreenWrapper>
  );
};

const styles = StyleSheet.create({
  listContainer: {
    paddingBottom: 20,
  },
  headerActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: theme.colors.background,
  },
  footerLoader: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 20,
  },
  footerText: {
    fontSize: hp(1.5),
    color: theme.colors.gray,
    marginLeft: 8,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 40,
    paddingVertical: 60,
  },
  emptyTitle: {
    fontSize: hp(2.5),
    fontWeight: '600',
    color: theme.colors.dark,
    marginBottom: 8,
  },
  emptyDescription: {
    fontSize: hp(1.6),
    color: theme.colors.gray,
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 24,
  },
  createButton: {
    backgroundColor: theme.colors.primary,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  createButtonText: {
    fontSize: hp(1.8),
    fontWeight: '600',
    color: theme.colors.white,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 40,
    paddingVertical: 60,
  },
  errorTitle: {
    fontSize: hp(2.2),
    fontWeight: '600',
    color: theme.colors.dark,
    marginBottom: 8,
  },
  errorDescription: {
    fontSize: hp(1.6),
    color: theme.colors.gray,
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 24,
  },
  retryButton: {
    backgroundColor: theme.colors.primary,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  retryButtonText: {
    fontSize: hp(1.8),
    fontWeight: '600',
    color: theme.colors.white,
  },
  headerButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  headerButtonText: {
    fontSize: hp(1.8),
    fontWeight: '600',
    color: theme.colors.primary,
  },
});

export default PollsList;
